import { Warning } from "@phosphor-icons/react"
import { Component, type ErrorInfo, type ReactNode } from "react"

interface State {
  hasError: boolean
  error: Error | null
}

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

const STALE_CHUNK_RELOAD_FLAG = "ok-stale-chunk-reload"

function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  const msg = error.message ?? ""
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    /Loading chunk \S+ failed/.test(msg) ||
    msg.includes("Loading CSS chunk")
  )
}

/**
 * Top-level error boundary. Route-level boundaries TanStack Router'ın
 * `errorComponent` prop'u ile yazılır.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    if (isStaleChunkError(error) && !sessionStorage.getItem(STALE_CHUNK_RELOAD_FLAG)) {
      sessionStorage.setItem(STALE_CHUNK_RELOAD_FLAG, "1")
      window.location.reload()
    }
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack)
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-ok-bg text-ok-foreground">
          <div className="max-w-md text-center space-y-4">
            <Warning size={48} weight="duotone" className="mx-auto text-ok-critical" />
            <h1 className="text-2xl font-semibold">Bir şeyler ters gitti</h1>
            <p className="text-sm text-ok-foreground-muted">
              Uygulama beklenmedik bir hata aldı. Lütfen sayfayı yenileyin. Sorun devam ederse ekibe
              bildirin.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-ok bg-ok-primary px-4 py-2 text-sm font-medium text-ok-primary-foreground hover:bg-ok-primary-hover transition-colors"
            >
              Sayfayı Yenile
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
